import toast from 'react-hot-toast';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export const COMPARISON_RANGES = [
  {
    id: 'precedingPeriod',
    label: 'Preceding period',
    callback: (range) => {
      // Get the number of days between the range.from and range.to
      const from = dayjs(range.from);
      const to = dayjs(range.to);
      const days = to.diff(from, 'days');

      return { from: dayjs(range.from).subtract(days, 'day').toISOString(), to: dayjs(range.to).subtract(days, 'day').toISOString() };
    },
  },
  {
    id: 'precedingPeriodMatchDayOfWeek',
    label: 'Preceding period (Match day of week)',
    callback: (range) => {
      const from = dayjs(range.from);
      const to = dayjs(range.to);
      const days = to.diff(from, 'days');

      // Get the day of the week for the selected range
      const fromDay = from.day();
      const toDay = to.day();

      // Get the last day of the week for the preceding period
      const precedingFrom = dayjs(range.from).subtract(days, 'day').day(fromDay);
      const precedingTo = dayjs(range.to).subtract(days, 'day').day(toDay);

      return { from: precedingFrom.toISOString(), to: precedingTo.toISOString() };
    },
  },
  {
    id: 'samePeriodLastMonth',
    label: 'Same period a month ago',
    callback: (range) => ({ from: dayjs(range.from).subtract(1, 'month').toISOString(), to: dayjs(range.to).subtract(1, 'month').toISOString() }),
  },
  {
    id: 'samePeriodThreeMonthsAgo',
    label: 'Same period 3 months ago',
    callback: (range) => ({ from: dayjs(range.from).subtract(3, 'month').toISOString(), to: dayjs(range.to).subtract(3, 'month').toISOString() }),
  },
  {
    id: 'samePeriodSixMonthsAgo',
    label: 'Same period 6 months ago',
    callback: (range) => ({ from: dayjs(range.from).subtract(6, 'month').toISOString(), to: dayjs(range.to).subtract(6, 'month').toISOString() }),
  },
  {
    id: 'samePeriodNineMonthsAgo',
    label: 'Same period 9 months ago',
    callback: (range) => ({ from: dayjs(range.from).subtract(9, 'month').toISOString(), to: dayjs(range.to).subtract(9, 'month').toISOString() }),
  },
  {
    id: 'samePeriodAYearAgo',
    label: 'Same period last year',
    callback: (range) => ({ from: dayjs(range.from).subtract(1, 'year').toISOString(), to: dayjs(range.to).subtract(1, 'year').toISOString() }),
  },
  {
    id: 'samePeriodAYearAgoMatchDayOfWeek',
    label: 'Same period last year (Match day of week)',
    callback: (range) => {
      // Get the day of the week for the selected range
      const fromDay = dayjs(range.from).day();
      const toDay = dayjs(range.to).day();

      // Get the last day of the week for the same period last year
      const precedingFrom = dayjs(range.from).subtract(1, 'year').day(fromDay);
      const precedingTo = dayjs(range.to).subtract(1, 'year').day(toDay);

      return { from: precedingFrom.toISOString(), to: precedingTo.toISOString() };
    },
  },
];

// All days here must be the number of days minus 1 because the code is inclusive of the selected day
export const PRESET_NUM_OF_DAYS = [
  { id: '7days', label: '7 days', days: 6 },
  { id: '14days', label: '14 days', days: 13 },
  { id: '28days', label: '28 days', days: 27 },
  { id: '30days', label: '30 days', days: 29 },
  { id: '60days', label: '60 days', days: 59 },
  { id: '90days', label: '90 days', days: 89 },
  { id: '120days', label: '120 days', days: 119 },
  { id: '180days', label: '180 days', days: 179 },
  { id: '365days', label: '365 days', days: 364 },
];

export const formatFromAndToQuery = (range) => {
  if (!range) {
    return '';
  }

  const { from, to } = range;

  if (!from || !to) {
    return '';
  }

  const formattedFrom = dayjs(from).format('YYYY-MM-DD');
  const formattedTo = dayjs(to).format('YYYY-MM-DD');

  return `from=${formattedFrom}&to=${formattedTo}`;
};

const CHART_OPTIONS = {
  legend: { position: 'top', textStyle: { color: 'white' }, maxLines: 2 },
  backgroundColor: '#2a214a',
  fontSize: 13,
  chartArea: {
    backgroundColor: '#2a214a',
  },
  colors: ['#54c7d7', '#e63757'], // Default blue and a contrasting orange in the same tone
  vAxis: {
    gridlines: { count: 0 },
    textPosition: 'none',
  },
  hAxis: {
    viewWindowMode: 'pretty',
    baselineColor: 'white',
    format: 'none',
    textStyle: {
      color: 'white',
      fontSize: 13,
    },
  },
  tooltip: { showColorCode: true },
};

const determineShowTextEvery = (range, multiplier = 1) => {
  // Get number of days from range.from to range.to
  const from = dayjs(range.from);
  const to = dayjs(range.to);
  const days = to.diff(from, 'days');

  if (days < 14) {
    return 1 * multiplier;
  } else if (days >= 14 && days <= 28) {
    return 2 * multiplier;
  } else if (days > 28 && days <= 90) {
    return 7 * multiplier;
  } else if (days > 90 && days <= 180) {
    return 14 * multiplier;
  } else if (days > 180 && days <= 365) {
    return 30 * multiplier;
  } else {
    return 2 * multiplier;
  }
};

export const getChartOptions = (type, range, isComparing) => {
  const multiplier = type === 'applications' ? 2 : 1;
  const showTextEvery = determineShowTextEvery(range, multiplier);

  switch (type) {
    case 'applications':
      return {
        ...CHART_OPTIONS,
        chartArea: {
          left: 10,
          right: 10,
        },
        bar: { groupWidth: isComparing ? '50%' : '20%' },
        hAxis: {
          ...CHART_OPTIONS.hAxis,
          showTextEvery,
        },
      };
    default:
      return {
        ...CHART_OPTIONS,
        chartArea: {
          left: 20,
          right: 20,
        },
        hAxis: {
          ...CHART_OPTIONS.hAxis,
          showTextEvery,
        },
      };
  }
};

export const calculatePercetageOfSecondValue = (first, second) => {
  if (isNaN(first) || isNaN(second)) {
    return '';
  }

  if (second === 0) {
    return '0%';
  }

  const percentage = ((first / second) * 100).toFixed(2);

  return `${percentage}%`;
};

export const calculatePercentageDifference = (first, second) => {
  if (isNaN(first) || isNaN(second)) {
    return '';
  }

  if ((first === 0 && second === 0) || first === second) {
    return '0%';
  }

  if (second === 0) {
    return '100%';
  }

  const absoluteDiff = first - second;

  const percentage = ((absoluteDiff / second) * 100).toFixed(2);

  if (percentage > 0) {
    return `+${percentage}%`;
  }

  return `${percentage}%`;
};

export const calculateTimeDifference = (first, second) => {
  // Parse the HH:mm:ss strings into Date objects
  if (first === null || second === null) {
    return '';
  }

  const parseTime = (time) => {
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return new Date(1970, 0, 1, hours, minutes, seconds);
  };

  const firstTime = parseTime(first);
  const secondTime = parseTime(second);

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = firstTime - secondTime;

  // Convert the difference from milliseconds to seconds
  const differenceInSeconds = differenceInMilliseconds / 1000;

  // Return a string with + or - depending on the difference between two times
  if (differenceInSeconds > 0) {
    return `+${differenceInSeconds}s`;
  }

  return `${differenceInSeconds}s`;
};

export const calculateNumberDifference = (first, second) => {
  if (first === null || second === null) {
    return '';
  }

  if (first === 0 && second === 0) {
    return 0;
  }

  const difference = Number(first) - Number(second);

  if (difference > 0) {
    return `+${difference.toFixed(2)}`;
  }

  return difference.toFixed(2);
};

export const calculateDifference = (first, second, type, metric) => {
  switch (type) {
    case 'TYPE_FLOAT':
      const firstFloat = formatValue(first, 'TYPE_FLOAT', metric);
      const secondFloat = formatValue(second, 'TYPE_FLOAT', metric);
      return calculateNumberDifference(firstFloat, secondFloat);
    case 'TYPE_SECONDS':
      const firstTime = formatValue(first, 'TYPE_SECONDS');
      const secondTime = formatValue(second, 'TYPE_SECONDS');
      return calculateTimeDifference(firstTime, secondTime);
    default:
      return calculatePercentageDifference(first, second);
  }
};

export const formatString = (str, type, metric) => {
  const result = formatValue(str, type, metric);

  if (!result) return result;

  if (metric === 'bounceRate') {
    return `${result}%`;
  }

  return result;
};

export const formatValue = (str, type, metric) => {
  if (type === 'TYPE_FLOAT') {
    if (isNaN(parseFloat(str))) {
      return null;
    }

    switch (metric) {
      case 'bounceRate':
        const percentage = parseFloat(str) * 100;
        return percentage.toFixed(2);
      default:
        return parseFloat(str).toFixed(2);
    }
  }

  if (type === 'TYPE_SECONDS') {
    if (isNaN(parseInt(str))) {
      return null;
    }
    // Using DayJS to format seconds to HH:mm:ss
    return dayjs.duration(parseInt(str), 'seconds').format('HH:mm:ss');
  }

  return str;
};

export const handleExport = async (isExporting, setIsExporting, exportEndpoint, body) => {
  if (isExporting) return;

  setIsExporting(true);
  const toastId = toast.loading('Preparing your export...', { duration: Infinity });

  try {
    const res = await fetch(`${exportEndpoint}.json`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error('Response not ok.');

    const data = await res.json();

    if (data.redirect_url) {
      toast.dismiss(toastId);
      window.location.href = data.redirect_url;
    }
  } catch (error) {
    toast.dismiss(toastId);
    toast.error('There was a problem exporting the data. Please try again.');
  } finally {
    setIsExporting(false);
  }
};
